import { useContext, useEffect, useRef, useState } from "react";
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from "react-intl";
import { Marker as LeafletMarker } from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap, Rectangle, SVGOverlay} from "react-leaflet";
import FunctionBox from "./FunctionBox";
import { LocationReducerActionType, LocationData } from "../reducers/locationReducer";
import { DeviceContext } from "../contexts/DeviceContext";
import { LocationContext } from "../contexts/LocationContext";
import { CONSTANT } from "../utils/constants";
import { useAsyncGet, useAsyncGetAddress } from "../utils/fetchUtils";
import "leaflet/dist/leaflet.css";
import "../styles/LeafletMap.css";

const defaultLocation: [number, number] = [-48.526, -123.236]; // nemo

const MapMarker = () => {
  // for UI
  const [location, setLocation] = useState<[number, number]>([-1, -1]);
  const [updateTime, setUpdateTime] = useState(-1);
  const [displayAddress, setDisplayAddress] = useState("");

  const { device } = useContext(DeviceContext);
  const { locationState } = useContext(LocationContext);
  const { formatMessage } = useIntl();
  const markerRef = useRef<LeafletMarker>(null);
  const leafletMap = useMap();

  const not_found_text = formatMessage({ id: "location_not_found" });
  const activeDevice = device.filter(dev => dev.isActive === true);

  useEffect(() => {
    const setMarkerLocation = (latlng: [number, number]) => {
      setLocation(latlng);
      leafletMap.setView(latlng);
      leafletMap.setMaxBounds([[latlng[0], latlng[1]], [latlng[0], latlng[1]]]);
      markerRef.current?.setLatLng(latlng);
      markerRef.current?.isPopupOpen() ? null : markerRef.current?.openPopup(latlng);
    };

    if(activeDevice[0]?.uniqueID) {
      if(activeDevice[0].remainDays > 0 && locationState[activeDevice[0].uniqueID]) { // license active
        const { latitude, longitude } = locationState[activeDevice[0].uniqueID];
        if(latitude && longitude) {
          setMarkerLocation([latitude, longitude]);
          locationState[activeDevice[0].uniqueID].address ? setDisplayAddress(locationState[activeDevice[0].uniqueID].address!): null;
        } else {
          setMarkerLocation(defaultLocation);
          setDisplayAddress(not_found_text);
        }
        locationState[activeDevice[0].uniqueID].locationTimestamp ? setUpdateTime(locationState[activeDevice[0].uniqueID].locationTimestamp!) : setUpdateTime(-1);
      } else {
        // expired
        setMarkerLocation(defaultLocation);
        setDisplayAddress(not_found_text);
        setUpdateTime(-1);
      }
    }
  }, [activeDevice[0], locationState]);

  return (
    <Marker ref={ markerRef } position={ location }>
      <Popup>
        <div className="mapmarker-address">{ displayAddress }</div>
        { updateTime !== -1 ? ( <div className="mapmarker-lastupdatetime">
                                  <FormattedDate
                                    value={ new Date(updateTime) }
                                    year="numeric"
                                    month="long"
                                    day="2-digit"
                                  />&nbsp;
                                  <FormattedTime value={ new Date(updateTime) }/>
                                </div> ) 
                            : null }
        <FunctionBox />
      </Popup>
    </Marker>
  );
};

const MapMask = ({ enable, showText }: { enable: boolean, showText: boolean }) => {
  const leafletMap = useMap();
  useEffect(()=> {
    if(enable) {
      leafletMap.setZoom(2);
      leafletMap.dragging.disable();
      leafletMap.zoomControl.remove();
      leafletMap.touchZoom.disable();
      leafletMap.doubleClickZoom.disable();
      leafletMap.scrollWheelZoom.disable();
      leafletMap.boxZoom.disable();
      leafletMap.keyboard.disable();
      leafletMap.tap?.disable();
    } else {
      leafletMap.setZoom(16);
      leafletMap.dragging.enable();
      leafletMap.zoomControl.addTo(leafletMap);
      leafletMap.touchZoom.enable();
      leafletMap.doubleClickZoom.enable();
      leafletMap.scrollWheelZoom.enable();
      leafletMap.boxZoom.enable();
      leafletMap.keyboard.enable();
      leafletMap.tap?.enable();
    }
  });

  return (
    <>
      { enable ? <Rectangle bounds={ [[-180, -1260], [180, 1260]] } pathOptions={ { color: '#dddddd', fillOpacity: 0.7 } } /> : null }
      { showText ? (  <SVGOverlay className="leafletmap-masktext" bounds={ [[-180, -360], [180, 360]] }>
                        <text x="50%" y="50%" textAnchor="middle">
                          <FormattedMessage id='map_no_device' />
                        </text>
                      </SVGOverlay> )
                 : null }
    </>
  );
};

const LeafletMap = () => {
  const { device } = useContext(DeviceContext);
  const { locationState, locationDispatch } = useContext(LocationContext);
  const [showMarker, setShowMarker] = useState(false);
  const [location, setLocation] = useState<[number, number]>([-1, -1]);
  const [enableMask, setEnableMask] = useState(false);
  const [mapClass, setMapClass] = useState('leafletmap leafletmap-nodevice');
  const activeDevice = device.filter(dev => dev.isActive === true);
  // const defaultLocation: [number, number] = [-48.526, -123.236]; // nemo

  useEffect(() => {
    if(activeDevice[0]?.uniqueID) {
      if(locationState[activeDevice[0].uniqueID]) {
        const { latitude, longitude } = locationState[activeDevice[0].uniqueID];
        setMapClass('leafletmap');
        latitude && longitude ? setLocation([latitude, latitude]) : setLocation(defaultLocation);
        setShowMarker(true);
        setEnableMask(activeDevice[0].remainDays < 0 || !(latitude && longitude));
      } else {
        useAsyncGet(`${CONSTANT.URL.API}/${CONSTANT.API.DEVICE_LOCATION}`, { UniqueID: activeDevice[0].uniqueID }).then((data: { location: LocationData } ) => {
          if(data instanceof Error) {
            const payload = { uid: activeDevice[0].uniqueID, location: {} };
            locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
          } else {
            const { location } = (data as { location: LocationData });
            if(location.latitude && location.longitude) {
              useAsyncGetAddress(location.latitude, location.longitude).then( address => {
                if( address instanceof Error) {
                  const payload = { uid: activeDevice[0].uniqueID, location: { ...location, address: "" } };
                  locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
                } else {
                  const payload = { uid: activeDevice[0].uniqueID, location: { ...location, address } };
                  locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
                }
              });
            } else {
              const payload = { uid: activeDevice[0].uniqueID, location };
              locationDispatch({ type: LocationReducerActionType.UPDATE, payload });
            }
          }
        });
      }
    } else {
      // no active device
      setMapClass('leafletmap leafletmap-nodevice');
      setLocation(defaultLocation);
      setShowMarker(false);
      setEnableMask(true);
    }
  }, [activeDevice[0], locationState]);
  
  return (
    <div className={ mapClass }>
      <MapContainer center={ location } zoom={ 16 } scrollWheelZoom={ true } minZoom={ 2 } maxZoom={ 18 }>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        { showMarker ? <MapMarker /> : null }
        <MapMask enable={ enableMask } showText={ !showMarker }/>
      </MapContainer>
    </div>
  );
};

export default LeafletMap;
